// app/components/FooterContact.tsx
"use client";

import { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid, Modal, IconButton } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import ContactForm from "./ContactForm";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";
import CloseIcon from "@mui/icons-material/Close";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function Contact() {
  const t = useTranslations("Contact");
  const locale = useLocale();

  const [openWeChatModal, setOpenWeChatModal] = useState(false);
  const handleWeChatModalOpen = () => setOpenWeChatModal(true);
  const handleWeChatModalClose = () => setOpenWeChatModal(false);

  // ✅ WhatsApp Modal state
  const [openWhatsAppModal, setOpenWhatsAppModal] = useState(false);
  const handleWhatsAppModalOpen = () => setOpenWhatsAppModal(true);
  const handleWhatsAppModalClose = () => setOpenWhatsAppModal(false);

  const [pagePath, setPagePath] = useState<string>("/");

  useEffect(() => {
    if (typeof window === "undefined") return;
    setPagePath(window.location.pathname);
  }, []);

  // ---- default จาก i18n / static asset (ดีต่อ SEO + SSR) ----
  const defaultTitle = t("title");
  const defaultSubtitle = t("subtitle");
  const defaultAddress = t("address");
  const defaultOpen = t("open");
  const defaultEmail = t("email");
  const defaultTel = t("tel");
  const defaultPhoneLabel = t("phone");

  const defaultEmailIcon = "/assets/email-icon.svg";
  const defaultPhoneIcon = "/assets/phone-icon.svg";
  const defaultLineIcon = "/assets/line.svg";
  const defaultWechatIcon = "/assets/wechat.svg";
  const defaultWhatsappIcon = "/assets/whatsapp.svg";

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [address, setAddress] = useState(defaultAddress);
  const [open, setOpen] = useState(defaultOpen);
  const [email, setEmail] = useState(defaultEmail);
  const [tel, setTel] = useState(defaultTel);
  const [phoneLabel, setPhoneLabel] = useState(defaultPhoneLabel);

  const [emailIcon, setEmailIcon] = useState(defaultEmailIcon);
  const [phoneIcon, setPhoneIcon] = useState(defaultPhoneIcon);
  const [lineIcon, setLineIcon] = useState(defaultLineIcon);
  const [wechatIcon, setWechatIcon] = useState(defaultWechatIcon);
  const [whatsappIcon, setWhatsappIcon] = useState(defaultWhatsappIcon);

  // เมื่อ locale เปลี่ยน → reset กลับค่า default ก่อน
  useEffect(() => {
    setTitle(defaultTitle);
    setSubtitle(defaultSubtitle);
    setAddress(defaultAddress);
    setOpen(defaultOpen);
    setEmail(defaultEmail);
    setTel(defaultTel);
    setPhoneLabel(defaultPhoneLabel);

    setEmailIcon(defaultEmailIcon);
    setPhoneIcon(defaultPhoneIcon);
    setLineIcon(defaultLineIcon);
    setWechatIcon(defaultWechatIcon);
    setWhatsappIcon(defaultWhatsappIcon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (!pagePath) return;

    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${
            process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID
          }&path=${encodeURIComponent(pagePath)}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback เผื่อบาง row ที่ยังไม่มี cmsKey แต่ selector ระบุ data-cms-key
          if (!key && e.selector) {
            const match = e.selector.match(/data-cms-key="([^"]+)"/);
            if (match) key = match[1];
          }

          if (key) {
            map[key] = e.value;
          }
        });

        // text fields
        if (map["contact.title"]) setTitle(map["contact.title"]);
        if (map["contact.subtitle"]) setSubtitle(map["contact.subtitle"]);
        if (map["contact.address"]) setAddress(map["contact.address"]);
        if (map["contact.open"]) setOpen(map["contact.open"]);
        if (map["contact.email"]) setEmail(map["contact.email"]);
        if (map["contact.tel"]) setTel(map["contact.tel"]);
        if (map["contact.phoneLabel"])
          setPhoneLabel(map["contact.phoneLabel"]);

        // icon images
        if (map["contact.icon.email"]) setEmailIcon(map["contact.icon.email"]);
        if (map["contact.icon.phone"]) setPhoneIcon(map["contact.icon.phone"]);
        if (map["contact.icon.line"]) setLineIcon(map["contact.icon.line"]);
        if (map["contact.icon.wechat"])
          setWechatIcon(map["contact.icon.wechat"]);
        if (map["contact.icon.whatsapp"])
          setWhatsappIcon(map["contact.icon.whatsapp"]);
      } catch (err) {
        console.warn("[FooterContact] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box id="contact" className="footer-contact-wrap" sx={{
      py: 10
    }}>
      <Container className="container">
        <Grid
          container
          spacing={10}
          sx={{
            display: "flex",
            alignItems: "center"
          }}>
          <Grid size={{ xs: 12, md: 7 }} data-aos="fade-up">
            <ContactForm />
          </Grid>

          <Grid size={{ xs: 12, md: 5 }} className="contact-info">
            <Typography
              variant="h2"
              data-heading-tag="H2"
              data-aos="fade-up"
              data-cms-key="contact.title"
              sx={{
                mb: 2,
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {title}
            </Typography>

            <Typography
              variant="subtitle1"
              data-heading-tag="H6"
              data-aos="fade-up"
              data-cms-key="contact.subtitle"
              sx={{
                mb: 2,
                fontWeight: "600",
                fontSize: "1.25rem"
              }}>
              {subtitle}
            </Typography>

            <Typography
              variant="body1"
              data-aos="fade-up"
              data-cms-key="contact.address"
              sx={{
                mb: 3
              }}
            >
              {address}
            </Typography>

            <Typography
              variant="body1"
              data-aos="fade-up"
              data-cms-key="contact.open"
              sx={{
                mb: 3,
                fontWeight: "600"
              }}>
              {open}
            </Typography>

            <Typography
              variant="body1"
              data-aos="fade-up"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 2
              }}>
              <Image
                src={emailIcon}
                width={30}
                height={30}
                title="tapioca email"
                alt="email-icon"
                data-cms-key="contact.icon.email"
              />
              <Link
                href={`mailto:${email}`}
                passHref
                data-cms-key="contact.email"
              >
                {email}
              </Link>
            </Typography>

            <Typography
              variant="body1"
              data-aos="fade-up"
              sx={{
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 2
              }}>
              <Image
                src={phoneIcon}
                width={30}
                height={30}
                title="phone-icon"
                alt="phone-icon"
                data-cms-key="contact.icon.phone"
              />
              <Link
                href={`tel:${tel}`}
                passHref
                data-cms-key="contact.phoneLabel"
              >
                {phoneLabel}
              </Link>
            </Typography>

            {/* ==== โซเชียล ==== */}
            <Box
              data-aos="fade-up"
              className="social-icons"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2
              }}>
              {/* LINE (ลิงก์ตรง) */}
              <a
                href="https://line.me/ti/p/A4gxe3dGz3"
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label="LINE"
                style={{ display: "inline-flex" }}
              >
                <Image
                  src={lineIcon}
                  width={80}
                  height={80}
                  title="line"
                  alt="line"
                  data-cms-key="contact.icon.line"
                />
              </a>

              {/* WeChat - IconButton + Modal */}
              <IconButton
                onClick={handleWeChatModalOpen}
                aria-label="Open WeChat QR Code"
                color="primary"
                sx={{ width: 80, height: 80 }}
              >
                <Image
                  src={wechatIcon}
                  width={80}
                  height={80}
                  title="wechat"
                  alt="Open WeChat QR Code for mmora7445"
                  data-cms-key="contact.icon.wechat"
                />
              </IconButton>

              {/* WhatsApp - IconButton + Modal เหมือน WeChat */}
              <IconButton
                onClick={handleWhatsAppModalOpen}
                aria-label="Open WhatsApp QR Code"
                color="primary"
                sx={{ width: 80, height: 80 }}
              >
                <Image
                  src={whatsappIcon}
                  width={80}
                  height={80}
                  title="whatsapp"
                  alt="Open WhatsApp QR Code"
                  data-cms-key="contact.icon.whatsapp"
                />
              </IconButton>
            </Box>
            {/* ==== /โซเชียล ==== */}
          </Grid>
        </Grid>
      </Container>
      {/* WeChat QR Code Modal */}
      <Modal
        open={openWeChatModal}
        onClose={handleWeChatModalClose}
        aria-labelledby="wechat-qr-modal-title"
        aria-describedby="wechat-qr-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "70%", md: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2
            }}>
            <IconButton
              aria-label="close"
              onClick={handleWeChatModalClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography id="wechat-qr-modal-description" sx={{ mt: 2, mb: 1 }}>
            {renderWithLineBreaks(t("add_wechat"))}
          </Typography>
          <Box
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 2,
            }}
          >
            <Image
              src="/assets/wechat-qrcode.jpg"
              alt="WeChat QR Code"
              width={200}
              height={200}
              style={{ objectFit: "contain" }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1
            }}>
            {renderWithLineBreaks(t("wechat_id"))}
          </Typography>
        </Box>
      </Modal>
      {/* WhatsApp QR Code Modal */}
      <Modal
        open={openWhatsAppModal}
        onClose={handleWhatsAppModalClose}
        aria-labelledby="whatsapp-qr-modal-title"
        aria-describedby="whatsapp-qr-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "70%", md: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2
            }}>
            <IconButton
              aria-label="close"
              onClick={handleWhatsAppModalClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography id="whatsapp-qr-modal-description" sx={{ mt: 2, mb: 1 }}>
            {renderWithLineBreaks(t("add_whatsapp"))}
          </Typography>
          <Box
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 2,
            }}
          >
            <Image
              src="/assets/whatsapp-qrcode.jpg" // ⬅️ ใส่ไฟล์รูป QR ของ WhatsApp ตรงนี้
              alt="WhatsApp QR Code"
              width={200}
              height={200}
              style={{ objectFit: "contain" }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1
            }}>
            {renderWithLineBreaks(t("whatsapp_id"))}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
}
